/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-multi-comp */
import React, { memo, forwardRef, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  DateSelect,
  Dropdown,
  Modal,
  ModalHeader,
  ModalContent,
  Cross,
  Space,
  Button,
  Text,
  Grid,
  Row,
  Column,
  InputField,
  Ref,
} from '@cashfree-intl/coherent';

import useClickOutside from 'utils/hooks';
import {
  PLACEHOLDER_TEXT,
  SETTLE_NOW_SUCCESS,
  DATE_AND_TIME_FORMAT,
} from 'utils/constants';
import { roundOffNumber, addCurrencySymbol } from 'utils/common';

import CustomIcon from 'components/CustomIcons';

import { VENDOR_OD_MAPPER, TIME_DROPDOWN_OPTIONS } from '../constants';

const EasySplitSettleNow = ({
  open,
  closeModal,
  data,
  resetModalData,
  resetModalDisplay,
  getVendorOdBalance,
  initiateVendorOd,
}) => {
  const [dateValue, setDateValue] = useState({
    startDate: '',
    endDate: '',
  });
  const [timeValue, setTimeValue] = useState({
    startTime: '',
    endTime: '',
  });
  const [step, setStep] = useState(1);
  const [btnLoader, resetLoader] = useState(false);

  const modalRef = useRef(null);
  useClickOutside(modalRef, closeModal);

  const onStartDateSelect = startDate => {
    setDateValue({ ...dateValue, startDate });
  };

  const onEndDateSelect = endDate => {
    setDateValue({ ...dateValue, endDate });
  };

  const onTimeChange = (e, option) => {
    setTimeValue({ ...timeValue, [option.id]: option.value });
  };

  const filterStartTimeOptions = () => {
    if (
      dateValue.startDate &&
      moment(dateValue.startDate).date() === moment().date()
    )
      return TIME_DROPDOWN_OPTIONS.filter(
        option => parseInt(option.key, 10) < moment().hour() - 1,
      );
    return TIME_DROPDOWN_OPTIONS;
  };

  const filterEndTimeOptions = () => {
    if (
      dateValue.endDate &&
      moment(dateValue.startDate).date() === moment(dateValue.endDate).date()
    ) {
      const timeKey = TIME_DROPDOWN_OPTIONS.filter(
        option => option.value === timeValue.startTime,
      )[0].key;

      if (moment(dateValue.endDate).date() === moment().date()) {
        return TIME_DROPDOWN_OPTIONS.filter(
          option =>
            parseInt(timeKey, 10) < parseInt(option.key, 10) &&
            parseInt(option.key, 10) < moment().hour(),
        );
      }
      return TIME_DROPDOWN_OPTIONS.filter(
        option => parseInt(timeKey, 10) < parseInt(option.key, 10),
      );
    }

    if (
      dateValue.endDate &&
      moment(dateValue.startDate).date() !== moment(dateValue.endDate).date() &&
      moment(dateValue.endDate).date() === moment().date()
    )
      return TIME_DROPDOWN_OPTIONS.filter(
        option => parseInt(option.key, 10) < moment().hour(),
      );

    return TIME_DROPDOWN_OPTIONS;
  };

  const getPayload = () => {
    const startDate = moment(dateValue.startDate)
      .format()
      .split('T')[0];
    const endDate = moment(dateValue.endDate)
      .format()
      .split('T')[0];
    return {
      merchantVendorId: data.merchantVendorId,
      startDate: moment([startDate, timeValue.startTime].join('T')).format(),
      endDate: moment([endDate, timeValue.endTime].join('T')).format(),
    };
  };

  const searchVendorOnDemandData = () => {
    resetLoader(true);
    getVendorOdBalance({
      ...getPayload(),
      nextSettlementDate: data.nextSettlementDate,
      successCb: response => {
        const {
          merchantVendorId,
          existingVendorBalance,
          serviceCharge,
          serviceTax,
          nextSettlementDate,
        } = response;

        const obj = {
          merchantVendorId,
          existingVendorBalance: addCurrencySymbol(
            roundOffNumber(existingVendorBalance),
          ),
          serviceCharge: addCurrencySymbol(roundOffNumber(serviceCharge)),
          serviceTax: addCurrencySymbol(roundOffNumber(serviceTax)),
          nextSettlementDate: moment(nextSettlementDate).format(
            DATE_AND_TIME_FORMAT,
          ),
        };

        resetModalData(obj);
        setStep(2);
      },
      errorCb: closeModal,
      resetLoader,
    });
  };

  const onSettleNowClick = () => {
    resetLoader(true);
    initiateVendorOd({
      ...getPayload(),
      successCb: response => {
        const { addedOn, requestId, settlementId, status } = response;

        const obj = {
          addedOn: moment(addedOn).format(DATE_AND_TIME_FORMAT),
          requestId,
          settlementId,
          status: status.toUpperCase(),
        };

        resetModalDisplay(SETTLE_NOW_SUCCESS);
        resetModalData(obj);
        resetLoader(false);
      },
      errorCb: closeModal,
    });
  };

  const navigateBack = () => {
    if (!btnLoader) {
      setStep(1);
      resetModalData({
        nextSettlementDate: data.nextSettlementDate,
        merchantVendorId: data.merchantVendorId,
      });
    }
  };

  const disableStartTime = !dateValue.startDate || btnLoader;
  const disableEndDate =
    !(dateValue.startDate && timeValue.startTime) || btnLoader;
  const disableEndTime =
    !(dateValue.startDate && timeValue.startTime && dateValue.endDate) ||
    btnLoader;
  const disableSearchBtn =
    !dateValue.startDate ||
    !timeValue.startTime ||
    !dateValue.endDate ||
    !timeValue.endTime ||
    btnLoader;

  const renderStartDateTime = () => (
    <Space gap={3} alignItems="baseline">
      <DateSelect
        value={dateValue.startDate}
        max={moment()
          .endOf('day')
          .toDate()}
        trigger={forwardRef((props, ref) => (
          <Ref innerRef={ref}>
            <InputField
              fluid
              label="Start Date"
              type="text"
              placeholder="Select"
              disabled={btnLoader}
              value={
                dateValue.startDate
                  ? moment(dateValue.startDate).format('DD/MMM/YYYY')
                  : ''
              }
              {...props}
            />
          </Ref>
        ))}
        onSelect={onStartDateSelect}
      />
      <div className="time-dropdown">
        <div className="label">Start Time</div>
        <Dropdown
          id="startTime"
          placeholder="Select"
          options={filterStartTimeOptions()}
          value={timeValue.startTime}
          onChange={onTimeChange}
          selection
          icon="chevron down"
          disabled={disableStartTime}
        />
      </div>
    </Space>
  );

  const renderEndDateTime = () => (
    <Space gap={3} alignItems="baseline" className="mb-3">
      <DateSelect
        value={dateValue.endDate}
        min={moment(dateValue.startDate).toDate()}
        max={moment()
          .endOf('day')
          .toDate()}
        trigger={forwardRef((props, ref) => (
          <Ref innerRef={ref}>
            <InputField
              fluid
              label="End Date"
              type="text"
              placeholder="Select"
              disabled={disableEndDate}
              value={
                dateValue.endDate
                  ? moment(dateValue.endDate).format('DD/MMM/YYYY')
                  : ''
              }
              {...props}
            />
          </Ref>
        ))}
        onSelect={onEndDateSelect}
      />
      <div className="time-dropdown">
        <div className="label">End Time</div>
        <Dropdown
          id="endTime"
          placeholder="Select"
          options={filterEndTimeOptions()}
          value={timeValue.endTime}
          onChange={onTimeChange}
          selection
          icon="chevron down"
          disabled={disableEndTime}
        />
      </div>
    </Space>
  );

  return (
    <Modal open={open} className="settle-now-vendor" ref={modalRef}>
      <ModalHeader>
        Vendor On-Demand Settlement{' '}
        <Cross
          onClick={closeModal}
          data-event-name="Form_EasySplitSettleNow_Icon_Close"
          data-ignore-inner-text="true"
        />
      </ModalHeader>
      <ModalContent>
        {step === 1 ? (
          <>
            <Text variant="p14" className="mr-btm-16">
              Transaction Range
            </Text>
            <Space direction="column" alignItems="center">
              {renderStartDateTime()}
              {renderEndDateTime()}

              <Button
                primary
                loading={btnLoader}
                onClick={searchVendorOnDemandData}
                disabled={disableSearchBtn}
                data-event-name="Primary_Button_Search"
              >
                Search
              </Button>
            </Space>
          </>
        ) : (
          <>
            <div
              className="back-navigate-left mr-btm-24"
              onClick={navigateBack}
              data-event-name="Button_Back"
            >
              <CustomIcon componentName="CHEVRONDOWN" direction="left" />
              Back
            </div>
            <Text strong>On Demand Settlement Details</Text>
            <Grid>
              {VENDOR_OD_MAPPER.map(obj => (
                <Row columns={2} key={obj.key}>
                  <Column>
                    <span className="text-light">{obj.displayName}</span>
                  </Column>
                  <Column>
                    {data[obj.key] ? (
                      <span className="text-dark w-600">{data[obj.key]}</span>
                    ) : (
                      PLACEHOLDER_TEXT
                    )}
                  </Column>
                </Row>
              ))}
            </Grid>
            <Space justifyContent="flex-end">
              <Button
                link
                onClick={closeModal}
                data-event-name="Form_EasySplitSettleNow_Button_Cancel"
              >
                Cancel
              </Button>
              <Button
                primary
                onClick={onSettleNowClick}
                loading={btnLoader}
                disabled={btnLoader}
                data-event-name="Primary_Button_SettleNow"
              >
                Settle Now
              </Button>
            </Space>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

EasySplitSettleNow.propTypes = {
  open: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  resetModalData: PropTypes.func.isRequired,
  resetModalDisplay: PropTypes.func.isRequired,
  getVendorOdBalance: PropTypes.func.isRequired,
  initiateVendorOd: PropTypes.func.isRequired,
};

export default memo(EasySplitSettleNow);
